import { useEffect } from 'react';
import { useLocalStore } from 'mobx-react';

export type TrackerOption = {
    /**
     * Called when the component first become visible according to the visibilityThreshold
     */
    onFirstEnter?: () => any;
    /**
     * Called when the component has been visible for the multiple of reportIntervalSec
     * @param currentDuration the current duration
     */
    onUpdateVisibleDuration?: (currentDuration: number) => any;
    /**
     * How often to report visible duration in sec
     */
    reportIntervalSec?: number;
    /**
     * When to report enter (the ratio of container size from 0 to 1)
     */
    visibilityThreshold?: number;
};

/**
 * Track visibility status (e.g. first enter, visible duration time) and report them through callbacks
 * specified in the options
 * @param ref HTMLElement the element to track
 * @param options the options
 */
export const useVisibilityTracker = (ref: HTMLElement, options: TrackerOption) => {
    const { reportIntervalSec = 1, visibilityThreshold = 0.2 } = options;
    const state = useLocalStore(() => ({
        windowFocused: true,
        entered: false,
        visible: false,
        visibleDuration: 0,
        durationTimer: null,
    }));
    useEffect(() => {
        const onBlur = () => {
            state.windowFocused = false;
            if (state.durationTimer) {
                clearInterval(state.durationTimer);
                state.durationTimer = null;
                state.visibleDuration = 0;
            }
        };
        window.addEventListener('blur', onBlur);
        return () => window.removeEventListener('blur', onBlur);
    }, []);
    useEffect(
        () => {
            if (!ref) {
                return;
            }
            const observer = new IntersectionObserver(
                ([entry]) => {
                    if (entry.isIntersecting) {
                        if (!state.windowFocused) {
                            return;
                        }
                        if (!state.entered) {
                            state.entered = true;
                            if (options.onFirstEnter) {
                                options.onFirstEnter();
                            }
                        }
                        if (!state.visible) {
                            state.visible = true;
                        }
                        if (!state.durationTimer) {
                            state.durationTimer = setInterval(() => {
                                const nextDuration = state.visibleDuration + reportIntervalSec;
                                state.visibleDuration = nextDuration;
                                if (options.onUpdateVisibleDuration) {
                                    options.onUpdateVisibleDuration(nextDuration);
                                }
                            }, reportIntervalSec * 1000);
                        }
                    } else {
                        state.visible = false;
                        if (state.durationTimer) {
                            clearInterval(state.durationTimer);
                            state.durationTimer = null;
                            state.visibleDuration = 0;
                        }
                    }
                },
                {
                    threshold: visibilityThreshold,
                }
            );

            observer.observe(ref);

            return function cleanUp() {
                observer.unobserve(ref);
                if (state.durationTimer) {
                    clearInterval(state.durationTimer);
                    state.durationTimer = null;
                    state.visibleDuration = 0;
                }
            };
        },
        [ref]
    );
};
